import * as React from "react";
import { ExternalLinkIcon, Loader2Icon, MoreVerticalIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@ibada/ui";
import { NOTIFICATION_INBOX_WINDOW_DAYS } from "@ibada/shared-types";
import { cn } from "@/shared/lib/utils";

import { useTranslations } from "@/i18n";
import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth";
import { NotificationList } from "@/features/notifications/components/notification-list";
import { NotificationListEnd } from "@/features/notifications/components/notification-list-end";
import { NotificationTimeBucket } from "@/features/notifications/lib/group-notifications-by-time";
import { flattenNotificationPages } from "@/features/notifications/lib/notification-inbox-pages";
import { type NotificationDto, NotificationStatus } from "@/features/notifications/model/notifications.types";
import {
  useInfiniteNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsUnreadCountQuery,
} from "@/features/notifications/services/notifications-query";
import { openNotificationAction } from "@/features/notifications/lib/open-notification-action";
import { buildCanonicalBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing";

// The bell panel loads 10 notifications at a time; the next 10 load when the bottom of the list scrolls into view.
const DROPDOWN_PAGE_SIZE = 10;

type NotificationDropdownProps = {
  onOpenChange?: (open: boolean) => void;
  /** When embedded in `SheetContent`, reserve top-right space for the built-in close control. */
  layout?: "popover" | "sheet";
};

export function NotificationDropdown({ onOpenChange, layout = "popover" }: NotificationDropdownProps) {
  const t = useTranslations("notifications");
  const navigate = useNavigate();
  const branchSlug = useActiveBranchSlug();
  const workspaceSlug = useActiveWorkspaceSlug();
  const inboxQuery = useInfiniteNotificationsQuery({ limit: DROPDOWN_PAGE_SIZE });
  const unreadCountQuery = useNotificationsUnreadCountQuery();
  const markReadMutation = useMarkNotificationReadMutation();
  const markAllMutation = useMarkAllNotificationsReadMutation();
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = inboxQuery;

  // Opening the panel no longer clears anything: the badge only drops when a notification is opened or marked read.

  const notificationsHref = buildCanonicalBranchAdminUrl(workspaceSlug, branchSlug, "/notifications");
  const visibleItems = React.useMemo(() => flattenNotificationPages(inboxQuery.data?.pages), [inboxQuery.data?.pages]);
  const hasUnread = (unreadCountQuery.data?.unreadCount ?? 0) > 0;
  const sectionLabels = React.useMemo<Record<NotificationTimeBucket, string>>(
    () => ({
      [NotificationTimeBucket.Today]: t("sections.today"),
      [NotificationTimeBucket.Yesterday]: t("sections.yesterday"),
      [NotificationTimeBucket.ThisWeek]: t("sections.thisWeek"),
      [NotificationTimeBucket.Earlier]: t("sections.earlier"),
    }),
    [t],
  );

  // Load the next page when the bottom of the list scrolls into view.
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root: scrollContainerRef.current, rootMargin: "120px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, visibleItems.length]);

  const handleItemSelect = React.useCallback(
    async (notification: NotificationDto) => {
      if (notification.status === NotificationStatus.Unread) {
        await markReadMutation.mutateAsync({ id: notification.id, status: notification.status });
      }

      openNotificationAction(notification, navigate);
      onOpenChange?.(false);
    },
    [markReadMutation, navigate, onOpenChange],
  );

  const handleMarkRead = React.useCallback(
    (notification: NotificationDto) => {
      void markReadMutation.mutateAsync({ id: notification.id, status: notification.status }).catch(() => {});
    },
    [markReadMutation],
  );

  return (
    <div
      className={cn(
        "flex w-full max-w-[28rem] flex-col gap-0 overflow-hidden",
        // Popover (desktop) is content-sized with a cap; the mobile sheet is
        // full-height, so fill it instead of leaving an empty bottom gap.
        layout === "sheet" ? "h-full" : "max-h-[min(78vh,42rem)]",
      )}
    >
      <div
        className={cn(
          "shrink-0 pt-2 pl-4",
          layout === "sheet" ? "pr-16" : "pr-4",
        )}
      >
        <div className="flex min-w-0 items-center justify-between gap-3">
          <h2 className="min-w-0 flex-1 truncate pr-2 text-base font-semibold">{t("dropdown.title")}</h2>
          <div className="flex shrink-0 items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild variant="ghost" size="icon-sm" aria-label={t("dropdown.viewAll")}>
                  <Link to={notificationsHref} onClick={() => onOpenChange?.(false)}>
                    <ExternalLinkIcon className="size-4" aria-hidden />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>
                {t("dropdown.viewAll")}
              </TooltipContent>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="icon-sm" className="shrink-0" aria-label={t("dropdown.moreOptions")}>
                  <MoreVerticalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  disabled={!hasUnread || markAllMutation.isPending}
                  onSelect={() => void markAllMutation.mutateAsync()}
                >
                  {t("actions.markAllRead")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto pb-3 pt-2">
        <NotificationList
          items={visibleItems}
          // Show the skeleton on a cold load AND while a background refetch is running with nothing to show yet,
          // so a slow/empty-cached fetch never flashes the "no notifications" empty state.
          isLoading={inboxQuery.isLoading || (inboxQuery.isFetching && visibleItems.length === 0)}
          isError={inboxQuery.isError}
          onRetry={() => void inboxQuery.refetch()}
          onItemSelect={handleItemSelect}
          onMarkRead={handleMarkRead}
          emptyTitle={t("empty.title")}
          emptyDescription={t("empty.description")}
          errorTitle={t("error.title")}
          errorDescription={t("error.description")}
          retryLabel={t("actions.retry")}
          compact
          itemSurface="plain"
          showWorkspaceContext={false}
          groupByTime
          sectionLabels={sectionLabels}
        />
        {visibleItems.length > 0 ? (
          hasNextPage ? (
            <div ref={loadMoreRef} className="text-muted-foreground flex items-center justify-center gap-2 py-4 text-xs">
              {isFetchingNextPage ? <Loader2Icon className="size-3.5 animate-spin" aria-hidden /> : null}
              <span className={isFetchingNextPage ? undefined : "sr-only"}>{t("endOfList.loadingMore")}</span>
            </div>
          ) : (
            <NotificationListEnd label={t("endOfList.all", { days: NOTIFICATION_INBOX_WINDOW_DAYS })} />
          )
        ) : null}
      </div>
    </div>
  );
}
